import {
  createContext,
  useMemo,
  type ReactNode,
  useContext,
} from "react";

export interface ActiveTenant {
  id: string;
  name: string | null;
}

export interface TenantOption {
  id: string;
  name: string | null;
  role: string;
}

interface TenantSwitcherContextValue {
  activeTenant: ActiveTenant | null;
  loadingTenant: boolean;
  tenants: TenantOption[];
  loadingTenants: boolean;
  isSwitchingTenant: boolean;
  switchTenant: (tenantId: string) => Promise<void>;
  impersonate: (tenantId: string, tenantName?: string) => Promise<void>;
  endImpersonation: () => Promise<void>;
}

const TenantSwitcherContext = createContext<
  TenantSwitcherContextValue | undefined
>(undefined);

const noop = async (): Promise<void> => {};

export function TenantSwitcherProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const value = useMemo<TenantSwitcherContextValue>(
    () => ({
      activeTenant: null,
      loadingTenant: false,
      tenants: [],
      loadingTenants: false,
      isSwitchingTenant: false,
      switchTenant: noop,
      impersonate: noop,
      endImpersonation: noop,
    }),
    []
  );

  return (
    <TenantSwitcherContext.Provider value={value}>
      {children}
    </TenantSwitcherContext.Provider>
  );
}

export function useTenantSwitcher(): TenantSwitcherContextValue {
  const context = useContext(TenantSwitcherContext);
  if (context === undefined) {
    throw new Error(
      "useTenantSwitcher must be used within a TenantSwitcherProvider"
    );
  }
  return context;
}
